/**
 * @ngdoc service
 * @name databaseApiService
 * @module flowingly.runner.services
 *
 * @description A service responsible solely for fetching databases data
 *
 * ## Notes
 * No Caching or formatting to be performed here.
 */

import ICustomTableName from '@Shared.Angular/@types/customDb/services/models/customTableName';
import angular from 'angular';
import { CustomdbServiceType } from '@Shared.Angular/flowingly.services/customdb.service';

angular
  .module('flowingly.runner.services')
  .factory('databaseApiService', databaseApiService);

databaseApiService.$inject = ['$http', 'customdbService'];

function databaseApiService(
  $http: angular.IHttpService,
  customdbService: CustomdbServiceType
) {
  const service = {
    databases: [] as ICustomTableName[],
    getDatabases: getDatabases,
    saveDatabase: saveDatabase,
    saveRecords: saveRecords,
    deleteDatabase: deleteDatabase,
    deleteRecord: deleteRecord,
    getRecords: getRecords,
    getColumns: getColumns
  };

  const databasesApiBaseUrl = customdbService.databasesApiBaseUrl;

  return service;

  //////////// Public API Methods

  function deleteDatabase(physicalTableName: string) {
    return $http({
      url: `${databasesApiBaseUrl}/${physicalTableName}`,
      method: 'DELETE'
    });
  }

  function deleteRecord(physicalTableName: string, itemId: string) {
    return $http({
      url: `${databasesApiBaseUrl}/${physicalTableName}/${itemId}`,
      method: 'DELETE'
    });
  }

  function saveDatabase(data) {
    return $http.post(databasesApiBaseUrl, data);
  }

  function saveRecords(physicalTableName: string, dataString: string) {
    dataString = dataString.replace(/'/g, "\\'").replace(/"/g, '\\"');
    return $http.post(
      `${databasesApiBaseUrl}/${physicalTableName}`,
      "'" + dataString + "'"
    );
  }

  function getDatabases(noSpinner = false) {
    return $http
      .get<ICustomTableName[]>(databasesApiBaseUrl, {
        noSpinner: noSpinner
      })
      .then(function (response) {
        angular.copy(response.data, service.databases);
        sortDatabasesByName();
        return response.data;
      });
  }

  function getRecords(physicalTableName: string, noSpinner = false) {
    return $http
      .get(`${databasesApiBaseUrl}/${physicalTableName}`, {
        noSpinner: noSpinner
      })
      .then(function (response) {
        return response.data;
      });
  }

  function getColumns(physicalTableName: string, noSpinner = false) {
    return $http
      .get(`${databasesApiBaseUrl}/columns/${physicalTableName}`, {
        noSpinner: noSpinner
      })
      .then(function (response) {
        return response.data;
      });
  }

  // PRIVATE METHODS /////////////////////////////////////////////////////////////////
  function sortDatabasesByName() {
    service.databases.sort((t1, t2) => {
      const t1NameToCompare = t1.name.trim().toLowerCase();
      const t2NameToCompare = t2.name.trim().toLowerCase();

      if (t1NameToCompare < t2NameToCompare) {
        return -1;
      }

      if (t1NameToCompare > t2NameToCompare) {
        return 1;
      }

      return 0;
    });
  }
}

export type DatabaseApiServiceType = ReturnType<typeof databaseApiService>;
